"use client";

import Link from "next/link";
import type { ReactNode } from "react";
import {
  BORDER,
  FONT_MONO,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
} from "@/components/ui/organisms/chord-practice/palette";

/**
 * Fil d'Ariane générique : `Ancêtre › Ancêtre › Feuille`.
 *
 * Extrait de `harmony-breadcrumb.tsx`, qui prenait une `HarmonyTonality` en dur
 * et n'était donc réutilisable nulle part. Les styles sont repris à
 * l'identique — la barre de 38 px, le fond, la séparation — pour que le studio
 * ne bouge pas d'un pixel au passage.
 *
 * Un maillon est soit un lien (`href`), soit une action (`onClick`) : le
 * studio navigue dans un état interne, la bibliothèque navigue dans des URL.
 * Forcer l'un des deux modèles aurait fait perdre à l'un ce que l'autre a de
 * naturel — le clic milieu ici, la vue conservée là-bas.
 */

export type BreadcrumbItem = {
  label: string;
  /** Destination. Exclusif avec `onClick`. */
  href?: string;
  /** Action. Exclusif avec `href`. */
  onClick?: () => void;
  /** Style du maillon, pour les racines qui se présentent en capitales. */
  style?: React.CSSProperties;
};

interface BreadcrumbProps {
  /** Les ancêtres, du plus général au plus proche. */
  items: BreadcrumbItem[];
  /** La feuille : la page courante, jamais cliquable. */
  current: string;
  /** Pastille affichée après la feuille (rôle fonctionnel, statut…). */
  badge?: string;
  /** Rendu avant le fil, typiquement un bouton de retour explicite. */
  leading?: ReactNode;
}

export function Breadcrumb({
  items,
  current,
  badge,
  leading,
}: BreadcrumbProps) {
  return (
    <nav
      aria-label="Fil d'Ariane"
      style={{
        height: 38,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "0 20px",
        borderBottom: `1px solid ${BORDER}`,
        background: "rgba(8,8,14,0.6)",
        fontFamily: FONT_MONO,
        fontSize: 11,
        letterSpacing: "0.06em",
      }}
    >
      {leading}

      {items.map((item, index) => (
        <span
          key={`${item.label}-${index}`}
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          {index > 0 && <Sep />}
          <Crumb item={item} />
        </span>
      ))}

      {items.length > 0 && <Sep />}
      <span style={{ color: TEXT }}>{current}</span>

      {badge !== undefined && (
        <span
          style={{
            marginLeft: 8,
            padding: "2px 8px",
            borderRadius: 4,
            background: "rgba(139,92,246,0.12)",
            border: `1px solid ${VIOLET_GLOW}`,
            color: TEXT_DIM,
            fontSize: 10,
            letterSpacing: "0.08em",
          }}
        >
          {badge}
        </span>
      )}
    </nav>
  );
}

function Crumb({ item }: { item: BreadcrumbItem }) {
  const baseColor = (item.style?.color as string | undefined) ?? TEXT_DIM;
  const style: React.CSSProperties = {
    background: "transparent",
    border: "none",
    padding: "2px 4px",
    margin: "0 -4px",
    borderRadius: 4,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "inherit",
    letterSpacing: "inherit",
    color: baseColor,
    transition: "background-color 120ms, color 120ms",
    ...item.style,
  };

  const hoverIn = (element: HTMLElement) => {
    element.style.background = "rgba(139,92,246,0.12)";
    element.style.color = VIOLET_BRIGHT;
  };

  const hoverOut = (element: HTMLElement) => {
    element.style.background = "transparent";
    element.style.color = baseColor;
  };

  if (item.href !== undefined) {
    return (
      <Link
        href={item.href}
        style={style}
        onMouseEnter={(event) => hoverIn(event.currentTarget)}
        onMouseLeave={(event) => hoverOut(event.currentTarget)}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={item.onClick}
      style={style}
      onMouseEnter={(event) => hoverIn(event.currentTarget)}
      onMouseLeave={(event) => hoverOut(event.currentTarget)}
    >
      {item.label}
    </button>
  );
}

function Sep() {
  return (
    <span aria-hidden style={{ color: TEXT_MUTE, opacity: 0.6 }}>
      ›
    </span>
  );
}
